import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";
import { Accordion, Button, Col, Row } from "react-bootstrap";
import ExerciseFeedback from "./ExerciseFeedback";
import ViewExerciseFeedback from "./ViewExerciseFeedback";

const SortableExerciseItem = ({
	exercise,
	index,
	activeKey,
	completedExercises,
	historyExercises,
	feedbackRefreshKey,
	isTherapist,
	selectedDate,
	videoRef,
	navigate,
	handleExerciseComplete,
	readOnlyPreview = false,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: exercise.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.6 : 1,
		overscrollBehavior: "none",
		position: "relative",
	};

	const isCompletedToday = completedExercises.has(exercise.id);
	const hasFeedbackHistory = historyExercises?.has(exercise.id) || isCompletedToday;
	const hasInstructions = Boolean(exercise.instructions?.trim());

	return (
		<Accordion.Item
			ref={setNodeRef}
			key={exercise.id}
			eventKey={index.toString()}
			className={[
				isCompletedToday ? "completed-exercise" : "",
				isTherapist ? "sortable-exercise-item-therapist" : "",
			].join(" ")}
			style={style}
		>
			{isTherapist && (
				<button
					type="button"
					className="exercise-drag-handle"
					aria-label={`Reorder activity ${exercise.name}`}
					{...attributes}
					{...listeners}
					onClick={(e) => e.stopPropagation()}
					onMouseDown={(e) => e.stopPropagation()}
					onTouchStart={(e) => e.stopPropagation()}
				>
					<span className="exercise-drag-icon">☰</span>
				</button>
			)}

			<Accordion.Header>
				<span className="exercise-header-content">
					<span className="exercise-header-name">
						{exercise.name}
					</span>

				{isCompletedToday && (
						<span
							className="exercise-completed-icon"
							aria-label="Completed"
							title="Completed"
						>
						<FontAwesomeIcon
							icon="fa-solid fa-circle-check"
							className="text-success"
						/>
					</span>
				)}
				</span>
			</Accordion.Header>

			<Accordion.Body>
				<Row>
					<Col md={hasInstructions ? 6 : 12}>
						<div className="mb-3">
							{!exercise.image_url && !exercise.video_url && (
								<h6>No images or videos for this activity.</h6>
							)}

							{exercise.image_url && (
								<div className="mb-3">
									<img
										src={exercise.image_url}
										alt={`${exercise.name} demonstration`}
										className="img-fluid rounded"
										style={{ maxHeight: "300px", objectFit: "contain" }}
									/>
								</div>
							)}

							{exercise.video_url && (
								<div className="mb-3">
									<h6 className="text-muted">Activity Video</h6>
									<video
										ref={videoRef}
										controls
										playsInline
										className="w-100 rounded"
										style={{ maxHeight: "300px" }}
										onError={(e) => {
											console.error("Video loading error:", e);
										}}
									>
										<source src={exercise.video_url} />
										Your browser does not support the video tag.
									</video>
								</div>
							)}
						</div>
					</Col>

					{hasInstructions && (
						<Col md={6}>
							<div className="mb-3">
								<h5 className="text-muted">Instructions</h5>
								<p className="text-dark preserve-whitespace">
									{exercise.instructions}
								</p>
							</div>
						</Col>
					)}
				</Row>

				{!isTherapist && !readOnlyPreview && (
					<Col xs={12}>
						{hasFeedbackHistory && (
							<div className="d-flex justify-content-end mb-3">
								<Button
									variant="outline-primary"
									className="d-flex align-items-center gap-2"
									onClick={() => navigate(`/exercise/${exercise.id}/feedback`)}
								>
									<i className="bi bi-clock-history"></i>
									See History
								</Button>
							</div>
						)}

						<ExerciseFeedback
							key={`${exercise.id}-${selectedDate}-${feedbackRefreshKey}`}
							exercise={exercise}
							selectedDate={selectedDate}
							onComplete={handleExerciseComplete}
							isCompleted={isCompletedToday}
						/>
					</Col>
				)}

				{isTherapist && (
					<Col xs={12}>
						{hasFeedbackHistory && (
							<Button
								variant="outline-primary"
								className="d-flex align-items-center gap-2 mb-3"
								onClick={() => navigate(`/exercise/${exercise.id}/feedback`)}
							>
								<i className="bi bi-list-ul"></i>
								View All Feedback
							</Button>
						)}

						<ViewExerciseFeedback
							exerciseId={exercise.id}
							selectedDate={selectedDate}
							hasFeedbackHistory={hasFeedbackHistory}
						/>
					</Col>
				)}
			</Accordion.Body>

			<style>{`
				.sortable-exercise-item-therapist .accordion-button {
					padding-left: 3.25rem;
				}

				.exercise-drag-handle {
					position: absolute;
					left: 0.75rem;
					top: 0.55rem;
					z-index: 3;
					display: flex;
					align-items: center;
					justify-content: center;
					width: 2rem;
					height: 2rem;
					border: 1px solid #dee2e6;
					border-radius: 0.375rem;
					background: #ffffff;
					color: #6c757d;
					cursor: grab;
					touch-action: none;
				}

				.exercise-drag-handle:active {
					cursor: grabbing;
				}

				.exercise-drag-icon {
					font-size: 1rem;
					line-height: 1;
				}

				.exercise-header-content {
					display: flex;
					flex: 1;
					align-items: center;
					gap: 0.75rem;
					min-width: 0;
				}

				.exercise-header-name {
					flex: 1;
					min-width: 0;
					overflow-wrap: anywhere;
				}

				.exercise-completed-icon {
					display: inline-flex;
					flex: 0 0 auto;
					align-items: center;
					justify-content: center;
					margin-left: auto;
					margin-right: -0.5rem;
					font-size: 2rem;
					line-height: 1;
				}

				.accordion-button::after {
					flex-shrink: 0;
				}

				@media (max-width: 767px) {
					.exercise-header-content {
						gap: 0.5rem;
					}

					.exercise-completed-icon {
						font-size: 1.75rem;
					}
				}
			`}</style>
		</Accordion.Item>
	);
};

export default SortableExerciseItem;

